import { Environment, getCurrentEnvironment, Region } from './environment';

export type DataCenterOption = { value: string; flag: string; name: string; description: string };

/**
 * The data centers an account can be created in. Picked once at signup (or a
 * Vercel install) and never changed, hence the warning beside the cards.
 */
export const DATA_CENTERS: DataCenterOption[] = [
  { value: 'us', flag: '🇺🇸', name: 'United States', description: 'For customers mostly outside Europe' },
  { value: 'eu', flag: '🇪🇺', name: 'Europe', description: 'For customers in the EU or UK' },
];

const DATA_CENTER_FLAGS: Record<string, string> = {
  us: '🇺🇸',
  eu: '🇪🇺',
};

/**
 * Formats an account's data center for display as a compact region badge, e.g.
 * "🇺🇸 US". Returns an empty string for an empty/unknown-to-us value so callers
 * can skip rendering; an unknown non-empty code falls back to the upper-cased
 * code without a flag.
 */
export function formatDataCenterShort(dataCenter: string | null | undefined): string {
  if (dataCenter == null || dataCenter === '') {
    return '';
  }
  const flag = DATA_CENTER_FLAGS[dataCenter] ?? '';
  return `${flag} ${dataCenter.toUpperCase()}`.trim();
}

/**
 * Adds the account's region to a documentation URL. The docs site reads
 * `region=eu` and shows the EU hosts and examples; without it an EU account
 * gets the US ones.
 */
export function docsUrlWithRegion(url: string, region: Region): string {
  const parsed = new URL(url);
  if (region === Region.EU) {
    parsed.searchParams.append('region', Region.EU);
  }
  return parsed.toString();
}

/**
 * The host segment of the tracking script the JavaScript snippet loads —
 * `https://assets.customer.io/assets/<segment>.js`.
 */
export function trackAssetHost(region: Region): string {
  return region === Region.EU ? 'track-eu' : 'track';
}

/** The parts of `window.location` a development host resolver reads. */
export interface TrackApiLocation {
  protocol: string;
  host: string;
  hostname: string;
}

/**
 * Where a local stack serves the track API.
 *
 * A devbox names its hosts `fly-<name>` or `fly.<domain>` and answers on the
 * matching `track-` host; anything else — a tunnelled devbox, plain localhost
 * — answers on port 9100. Only knowable from the live location, which is why
 * every development host in this module resolves per call rather than from a
 * table.
 */
export function developmentTrackApiHost(location: TrackApiLocation): string {
  if (location.host.startsWith('fly-')) {
    return `${location.protocol}//${location.host.replace('fly-', 'track-')}`;
  }
  if (location.host.startsWith('fly.')) {
    return `${location.protocol}//${location.host.replace('fly.', 'track.')}`;
  }
  return `${location.protocol}//${location.hostname}:${LOCAL_TRACK_PORT}`;
}

/** The port the track API listens on in a local checkout. */
const LOCAL_TRACK_PORT = 9100;

/**
 * The host a webhook trigger's endpoint is served from, for one data center.
 *
 * Production splits by data center; standalone serves both from one staging
 * host; development derives the host from where the app is being served,
 * because a devbox's track host is its own host with the `fly-` prefix swapped
 * for `track-`, and a local checkout reaches it on a fixed port.
 *
 * `location` is a parameter rather than a read of `window` so every branch is
 * testable without standing up a location, and `environment` defaults to the
 * current one so callers state only what they know.
 */
export function appApiHostForDataCenter(
  dataCenter: string,
  location: TrackApiLocation = window.location,
  environment: Environment = getCurrentEnvironment(),
): string {
  switch (environment) {
    case Environment.PRODUCTION:
      return dataCenter === Region.EU ? 'https://api-eu.customer.io' : 'https://api.customer.io';
    case Environment.STANDALONE:
      return 'https://track-gce-staging.customer.io';
    case Environment.DEVELOPMENT:
      return developmentTrackApiHost(location);
    default: {
      const _exhaustive: never = environment;
      return String(_exhaustive);
    }
  }
}

/** The hosts one service answers on outside a local stack. */
type ServiceHosts = {
  production: Record<Region, string>;
  /** Standalone serves both data centers from one staging host. */
  standalone: string;
};

const TRACK_API_HOSTS: ServiceHosts = {
  production: { [Region.US]: 'https://track.customer.io', [Region.EU]: 'https://track-eu.customer.io' },
  standalone: 'https://track-gce-staging.customer.io',
};

/** The forms hosts sit on their own domain rather than under `customer.io`. */
const FORMS_HOSTS: ServiceHosts = {
  production: { [Region.US]: 'https://customerioforms.com', [Region.EU]: 'https://eu.customerioforms.com' },
  standalone: 'https://staging.customerioforms.com',
};

/**
 * One service's host for a data center. A local stack serves the track API and
 * the forms endpoints alike from its track host, which only the live location
 * can say.
 */
function serviceHost(
  hosts: ServiceHosts,
  dataCenter: Region,
  location: TrackApiLocation,
  environment: Environment,
): string {
  switch (environment) {
    case Environment.PRODUCTION:
      return hosts.production[dataCenter];
    case Environment.STANDALONE:
      return hosts.standalone;
    case Environment.DEVELOPMENT:
      return developmentTrackApiHost(location);
    default: {
      const _exhaustive: never = environment;
      return String(_exhaustive);
    }
  }
}

/** Where the track API answers, which is where a form submitted from a server posts. */
export function trackApiHostForDataCenter(
  dataCenter: Region,
  location: TrackApiLocation = window.location,
  environment: Environment = getCurrentEnvironment(),
): string {
  return serviceHost(TRACK_API_HOSTS, dataCenter, location, environment);
}

/** Where a web page's form posts: the forms endpoints, and the JavaScript snippet's API. */
export function formsHostForDataCenter(
  dataCenter: Region,
  location: TrackApiLocation = window.location,
  environment: Environment = getCurrentEnvironment(),
): string {
  return serviceHost(FORMS_HOSTS, dataCenter, location, environment);
}

/**
 * Where the JavaScript snippet loads `forms.js` from. Production serves it
 * beside the forms API; every other tier loads the staging build, because a
 * local stack does not serve the script.
 */
export function formsScriptHostForDataCenter(
  dataCenter: Region,
  location: TrackApiLocation = window.location,
  environment: Environment = getCurrentEnvironment(),
): string {
  return environment === Environment.PRODUCTION
    ? formsHostForDataCenter(dataCenter, location, environment)
    : FORMS_HOSTS.standalone;
}

/**
 * Where a webhook trigger listens, or an empty string before its endpoint has
 * been minted.
 *
 * An empty string rather than null because the URL is shown and copied as
 * text: a campaign whose endpoint is still being created has nothing to show,
 * which is the same nothing the spinner stands in front of.
 */
export function webhookUrl(webhookId: string | null | undefined, host: string): string {
  if (webhookId == null || webhookId === '') {
    return '';
  }
  return `${host}/v1/webhook/${webhookId}`;
}
